import React, { useState } from 'react';
import ExcelScheduleImporter from './ExcelScheduleImporter';

interface ScheduleEvent {
  id: string;
  title: string;
  startTime: string; // Format: HH:MM
  endTime: string;   // Format: HH:MM
  location: string;
  date: string;      // Format: YYYY-MM-DD
  description?: string;
  type?: string;     // For color coding
}

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (events: ScheduleEvent[], importOption: ImportOption) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  currentScheduleSize: number;
}

// Import options for handling the imported data
export type ImportOption = 'add' | 'replace' | 'replace-dates';

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onImport, 
  onError, 
  onSuccess, 
  currentScheduleSize 
}) => {
  const [importedEvents, setImportedEvents] = useState<ScheduleEvent[]>([]);
  const [importOption, setImportOption] = useState<ImportOption>('add');
  const [previewVisible, setPreviewVisible] = useState(false);
  
  const handleEventsImported = (events: ScheduleEvent[]) => {
    setImportedEvents(events);
    setPreviewVisible(true);
  };
  
  const handleImportOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportOption(e.target.value as ImportOption);
  };
  
  const handleConfirmImport = () => {
    if (importedEvents.length > 0) {
      onImport(importedEvents, importOption);
      // Reset state
      setImportedEvents([]);
      setPreviewVisible(false);
      onClose();
    } else {
      onError('No events to import. Please upload a valid Excel file first.');
    }
  };
  
  const handleCancel = () => {
    // Reset state
    setImportedEvents([]);
    setPreviewVisible(false);
    onClose();
  };
  
  const handleBackToUpload = () => {
    setPreviewVisible(false);
    setImportedEvents([]);
  };
  
  // Sort events by date and time for preview - make a copy to avoid mutating state
  const sortedEvents = importedEvents.slice().sort((a, b) => {
    // Compare dates first
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    
    // If dates are the same, compare start times
    return a.startTime.localeCompare(b.startTime);
  });
  
  // Format date for display
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    
    return date.toLocaleDateString('en-GB', options);
  };
  
  // Format time for display (HH:MM to 12-hour format)
  const formatTime = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: previewVisible ? '90%' : '500px', width: previewVisible ? '90%' : 'auto' }}>
        <div className="modal-header">
          <h3>Import Schedule from Excel</h3>
          <button
            type="button"
            className="modal-close-btn"
            onClick={handleCancel}
          >
            <span className="icon-close">✖️</span>
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: '1.5rem' }}>
          {!previewVisible ? (
            <ExcelScheduleImporter 
              onImport={handleEventsImported}
              onError={onError}
              onSuccess={onSuccess}
            />
          ) : (
            <div className="import-preview">
              <h4>Import Preview</h4>
              <p className="preview-count">
                Found {importedEvents.length} events to import
              </p>
              
              <div className="import-options">
                <h5>Import Options</h5>
                <div className="option-group">
                  <div className="radio-option">
                    <input 
                      type="radio" 
                      id="option-add" 
                      name="importOption" 
                      value="add"
                      checked={importOption === 'add'}
                      onChange={handleImportOptionChange}
                    />
                    <label htmlFor="option-add">
                      Add to existing schedule ({currentScheduleSize} existing events)
                    </label>
                  </div>
                  
                  <div className="radio-option">
                    <input 
                      type="radio" 
                      id="option-replace" 
                      name="importOption" 
                      value="replace"
                      checked={importOption === 'replace'}
                      onChange={handleImportOptionChange}
                    />
                    <label htmlFor="option-replace">
                      Replace entire schedule
                    </label>
                  </div>
                  
                  <div className="radio-option">
                    <input 
                      type="radio" 
                      id="option-replace-dates" 
                      name="importOption" 
                      value="replace-dates"
                      checked={importOption === 'replace-dates'}
                      onChange={handleImportOptionChange}
                    />
                    <label htmlFor="option-replace-dates">
                      Replace events on the same dates only
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '1rem' }}>
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Event</th>
                      <th>Location</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEvents.length > 0 ? (
                      sortedEvents.map((event, index) => (
                        <tr key={`preview-${index}`}>
                          <td>{formatDate(event.date)}</td>
                          <td>{formatTime(event.startTime)} - {formatTime(event.endTime)}</td>
                          <td>{event.title}</td>
                          <td>{event.location}</td>
                          <td>{event.type || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                          No events found in the uploaded file.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button 
                  type="button" 
                  className="back-btn" 
                  onClick={handleBackToUpload}
                >
                  Back to Upload
                </button>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="import-btn" 
                  onClick={handleConfirmImport}
                  disabled={importedEvents.length === 0}
                >
                  Import {importedEvents.length} Events
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;